<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { useField, useForm } from 'vee-validate';
import {
  computed,
  nextTick,
  ref,
  watch,
} from 'vue';
import * as yup from 'yup';

import type {
  GrammarTextFieldExpose,
} from '@nora-monorepo/grammar-ui';
import {
  GrammarFooter,
  GrammarModal,
  GrammarPrimaryButton,
  GrammarSecondaryButton,
  GrammarTextField,
} from '@nora-monorepo/grammar-ui';

import { useTextPromptModalStore } from '#ui-shared/components/modals/text-prompt-modal/text-prompt-modal-store';

const emits = defineEmits(['confirm', 'cancel']);

const textPromptModalStore = useTextPromptModalStore();
const { handleConfirm, handleCancel } = textPromptModalStore;

const {
  isOpen,
  title,
  textFieldLabel,
  placeholder,
  initialValue,
  confirmButtonLabel,
  cancelButtonLabel,
  maxCharacters,
  requireNonEmptyValue,
  nonEmptyValueErrorMessage,
  validateValue,
  validationErrorMessage,
} = storeToRefs(textPromptModalStore);

const textFieldRef = ref<GrammarTextFieldExpose | null>(null);

const schema = computed(() => {
  let textSchema = yup.string();

  if (requireNonEmptyValue.value) {
    textSchema = textSchema.trim().required(nonEmptyValueErrorMessage.value);
  }

  if (maxCharacters.value) {
    textSchema = textSchema.max(maxCharacters.value, `Max characters is ${maxCharacters.value}`);
  }

  if (validateValue.value) {
    textSchema = textSchema.test(
      'custom-validation',
      validationErrorMessage.value ?? 'Invalid value',
      value => value === undefined || validateValue.value?.(value) === true,
    );
  }

  return yup.object({ text: textSchema });
});

const { handleSubmit, errors, resetForm } = useForm({
  validationSchema: schema,
  initialValues: {
    text: initialValue.value,
  },
});

const { value: text } = useField<string>('text');

const canSave = computed(() => !errors.value.text);

function onCancelClick() {
  text.value = '';
  handleCancel();
  emits('cancel');
}

const onConfirmClick = handleSubmit(async (values) => {
  await handleConfirm(values.text);
  text.value = '';
  emits('confirm', values.text);
});

const inputFieldType = computed(() => {
  return errors.value.text ? 'error' : 'default';
});

const errorMessage = computed(() => {
  return errors.value.text || '';
});

function onKeyDown(event: KeyboardEvent) {
  if (event.key === 'Enter' && canSave.value) {
    event.preventDefault();
    onConfirmClick();
  }
  else if (event.key === 'Escape') {
    event.preventDefault();
    onCancelClick();
  }
}

async function onModalShow() {
  await nextTick();
  requestAnimationFrame(() => {
    textFieldRef.value?.focus();
  });
}

watch(isOpen, (newVal) => {
  if (newVal) {
    resetForm({
      values: {
        text: initialValue.value,
      },
    });
  }
});

watch(initialValue, (newVal) => {
  text.value = newVal;
});
</script>

<template>
  <GrammarModal
    :is-open="isOpen"
    :show-close-button="true"
    :label="title"
    @show="onModalShow"
    @close="onCancelClick"
  >
    <template #content>
      <div class="pb-12">
        <GrammarTextField
          ref="textFieldRef"
          v-model:value="text"
          input-type="text"
          :label="textFieldLabel"
          :placeholder="placeholder"
          :form-associated="false"
          :counter="maxCharacters"
          :type="inputFieldType"
          :message="errorMessage"
          @keydown="onKeyDown"
        />
      </div>
    </template>

    <template #footer>
      <GrammarFooter>
        <template #left-button>
          <GrammarSecondaryButton
            :label="cancelButtonLabel"
            @click="onCancelClick"
          />
        </template>
        <template #right-button>
          <GrammarPrimaryButton
            :label="confirmButtonLabel"
            :disabled="!canSave"
            @click="onConfirmClick"
          />
        </template>
      </GrammarFooter>
    </template>
  </GrammarModal>
</template>
